import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Spinner,
  Circle,
  Select,
  Button,
  Badge,
  Checkbox,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Tooltip,
  Link,
  VStack,
  Code,
  useTheme,
  useColorMode,
  useToast,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

import {
  faChevronLeft,
  faChevronRight,
  faSearch,
  faHeartPulse,
  faTriangleExclamation,
  faArrowUpRightFromSquare,
  faBell,
  faBellSlash,
} from "@fortawesome/free-solid-svg-icons";
import { cqAPI, loadCustomers } from "../../lib/Auth";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../CallReport/CallReport.css";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import debounce from "lodash.debounce";
import CommentInput from "./CommentInput";

// Traffic-light colors for a PMSStatus health rating. Mirrors the swatches used
// in AdapterSetting/ClientTokenSettings so the meaning is consistent app-wide.
export const SYNC_HEALTH_COLORS = {
  healthy: "#4B9560",
  impaired: "#FF9641",
  failed: "#D64545",
  unknown: "#9CA3AF",
};

export const syncHealthColor = (status) =>
  SYNC_HEALTH_COLORS[status] || SYNC_HEALTH_COLORS.unknown;

// How often the listing silently re-polls the API while the page is visible.
const POLL_INTERVAL_MS = 60000;

// Selectable alert-mute durations for the per-business bell menu, in display
// order. The server validates the same set (plus "off" to unmute).
const MUTE_OPTIONS = [
  ["6h", "Mute for 6 hours"],
  ["1d", "Mute for 1 day"],
  ["1w", "Mute for 1 week"],
  ["until-healthy", "Mute until healthy"],
  ["forever", "Mute forever"],
];

// Badge and tooltip copy for a business's server-owned alert mute (null when
// not muted). `until` is null for the open-ended durations — "forever", and
// "until-healthy", which the worker lifts on recovery.
const muteSummary = (mute) => {
  if (!mute) {
    return {
      badge: null,
      tooltip: "Mute email & Slack alerts for this location.",
    };
  }
  const by = mute.by ? ` by ${mute.by}` : "";
  if (mute.duration === "until-healthy") {
    return {
      badge: "Muted until healthy",
      tooltip: `Alerts muted${by} until this location is healthy again — unmutes automatically on recovery.`,
    };
  }
  if (mute.until) {
    const until = moment(mute.until);
    // A mute ending today (e.g. a 6-hour one) needs the time — the bare date
    // would just name the current day.
    const untilText = until.isSame(moment(), "day")
      ? until.format("LT")
      : until.format("MMM D");
    return {
      badge: `Muted until ${untilText}`,
      tooltip: `Alerts muted until ${until.format("LLL")}${by}.`,
    };
  }
  return { badge: "Muted", tooltip: `Alerts muted forever${by}.` };
};

// Bell menu for muting/unmuting one business's PMS health alerts. Rendered
// beside (not inside) the accordion toggle so clicking it doesn't expand the
// row. `onChange` receives a MUTE_OPTIONS duration, or "off" to unmute.
const MuteMenu = ({ mute, onChange }) => (
  <Tooltip label={muteSummary(mute).tooltip} hasArrow placement="top">
    <Box mx={1}>
      <Menu isLazy>
        <MenuButton
          as={Button}
          size="sm"
          variant="ghost"
          colorScheme={mute ? "purple" : "gray"}
          aria-label={
            mute ? "Alerts muted — change or unmute" : "Mute alerts"
          }
        >
          <FontAwesomeIcon icon={mute ? faBellSlash : faBell} />
        </MenuButton>
        <MenuList>
          {MUTE_OPTIONS.map(([duration, label]) => (
            <MenuItem
              key={duration}
              // The active duration is greyed out — re-picking it would only
              // rewrite the same mute.
              isDisabled={mute?.duration === duration}
              onClick={() => onChange(duration)}
            >
              {label}
            </MenuItem>
          ))}
          {mute && (
            <>
              <MenuDivider />
              <MenuItem onClick={() => onChange("off")}>Unmute</MenuItem>
            </>
          )}
        </MenuList>
      </Menu>
    </Box>
  </Tooltip>
);

// End-user phrasing for each health rating, mirroring ClientTokenSettings.
export const SYNC_HEALTH_LABELS = {
  healthy: "Healthy — data is syncing normally.",
  impaired: "Impaired — recovering from issues detected within the last 60 minutes.",
  failed: "Failed — sync is not working. Please contact support.",
  unknown: "Unknown — sync status is not yet available.",
};

// Compute the [start, end] Date window for a named range, relative to the
// current moment. Recomputing on each call is what lets the window slide
// forward as time passes (the listing auto-refreshes), so we don't stay pinned
// to whenever the page was first opened. "Custom" yields [null, null] because
// the user picks explicit dates there.
const computeRangeDates = (rangeName) => {
  let start;
  let end;
  switch (rangeName) {
    case "Last 24 hours":
      start = moment().subtract(24, "hours");
      end = moment();
      break;
    case "Today":
      start = moment().startOf("day");
      end = moment().endOf("day");
      break;
    case "Yesterday":
      start = moment().subtract(1, "days").startOf("day");
      end = moment().subtract(1, "days").endOf("day");
      break;
    case "Last 7 days":
      start = moment().subtract(6, "days").startOf("day");
      end = moment().endOf("day");
      break;
    case "Last 4 weeks":
      start = moment().subtract(4, "weeks").startOf("day");
      end = moment().endOf("day");
      break;
    case "Custom":
    default:
      start = null;
      end = null;
  }
  return [start ? start.toDate() : null, end ? end.toDate() : null];
};

// Pretty-print a value as JSON for display, tolerating values that can't be
// serialized (e.g. circular references) by falling back to String().
const formatJson = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

// Everything on an error object other than the fields rendered separately:
// name/message/stack are shown in the Code block above, and `cause` is rendered
// by the recursion. Returns null when there are no additional fields.
const extraErrorFields = (error) => {
  if (!error || typeof error !== "object") {
    return null;
  }
  const omit = new Set(["name", "message", "stack", "cause"]);
  const rest = Object.fromEntries(
    Object.entries(error).filter(([key]) => !omit.has(key))
  );
  return Object.keys(rest).length > 0 ? rest : null;
};

// Recursively render an error's `cause` chain. Node.js wrapped exceptions nest
// the underlying error under `cause`, which can itself carry a `cause` — so we
// recurse without a depth cap (in practice the chain is only a few links deep).
const CauseChain = ({ cause, secondaryTextColor }) => {
  if (!cause) {
    return null;
  }
  const extras = extraErrorFields(cause);
  return (
    <Box mt={3} pl={3} borderLeftWidth="2px" borderColor="gray.300">
      <Text fontSize="xs" fontWeight="bold" color={secondaryTextColor} mb={1}>
        Caused by
      </Text>
      <Code
        display="block"
        whiteSpace="pre-wrap"
        fontSize="xs"
        p={3}
        borderRadius="md"
        overflowX="auto"
      >
        {cause.stack || `${cause.name || "Error"}: ${cause.message || ""}`}
      </Code>
      {extras && (
        <>
          <Text
            fontSize="xs"
            fontWeight="bold"
            color={secondaryTextColor}
            mt={2}
            mb={1}
          >
            Additional fields
          </Text>
          <Code
            display="block"
            whiteSpace="pre-wrap"
            fontSize="xs"
            p={3}
            borderRadius="md"
            overflowX="auto"
          >
            {formatJson(extras)}
          </Code>
        </>
      )}
      <CauseChain cause={cause.cause} secondaryTextColor={secondaryTextColor} />
    </Box>
  );
};

// Inline detail shown under an expanded PMSStatus row. Renders the same issue /
// error / cause-chain breakdown as the standalone detail page. The listing
// endpoint omits the heavy `issues[].errors` bodies (they can be many KB each
// and would blow past Mongo's document-size limit when grouped), so when a row
// is expanded we fetch the full record from the detail endpoint to surface the
// error bodies for impaired/failed entries. Also links out to the standalone
// detail page (a shareable landing page) in a new tab.
const PMSStatusInlineDetail = ({ record, onCommentsChange }) => {
  const theme = useTheme();
  const { colorMode } = useColorMode();
  const secondaryTextColor =
    colorMode === "light" ? theme.colors.gray[600] : theme.colors.gray[400];
  const cardBg =
    colorMode === "light" ? theme.colors.gray[100] : theme.colors.gray[600];
  const formatDate = (date) => (date ? moment(date).format("LLL") : "N/A");

  // Full record (with error bodies) fetched on expand. Until it loads we render
  // the summaries the listing already has; if the fetch fails we keep them too.
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const hasIssues = (record?.issues?.length || 0) > 0;

  useEffect(() => {
    // Healthy entries carry no issues/errors, so skip the extra round-trip.
    if (!hasIssues) {
      return undefined;
    }
    let cancelled = false;
    setLoadingDetail(true);
    cqAPI
      .get(`/monitoring/pms-status?id=${record._id}`)
      .then((response) => {
        if (!cancelled) {
          setDetail(response.data?.pmsStatus || null);
        }
      })
      .catch((error) => {
        // Degrade gracefully: keep showing the issue summaries without bodies.
        console.error("Error fetching PMS status error details:", error);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingDetail(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [record._id, hasIssues]);

  // Prefer the fully-detailed issues (which include the error bodies) once
  // loaded, falling back to the listing summaries while they load or if the
  // fetch failed.
  const issues = detail?.issues || record?.issues || [];

  return (
    <Box p={4}>
      <Flex justify="space-between" align="center" wrap="wrap" rowGap={2} mb={3}>
        <Flex align="center">
          <Text fontSize="md" fontWeight="bold">
            {issues.length === 0
              ? "No issues recorded"
              : `${issues.length} issue${issues.length === 1 ? "" : "s"}`}
          </Text>
          {loadingDetail && (
            <Tooltip label="Loading error details…" hasArrow placement="top">
              <Spinner size="sm" color="blue.500" ml={3} />
            </Tooltip>
          )}
        </Flex>
        <Tooltip
          label="Open this status detail in a new tab"
          hasArrow
          placement="top"
        >
          <Link
            href={`/monitoring/pmsstatus/${record._id}`}
            isExternal
            color="blue.500"
            fontSize="sm"
            // Don't let the link click toggle the row it lives under.
            onClick={(e) => e.stopPropagation()}
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            <Text as="span" ml={2}>
              Open detail
            </Text>
          </Link>
        </Tooltip>
      </Flex>

      {issues.length === 0 ? (
        <Text color={secondaryTextColor} fontSize="sm">
          No issues recorded — the adapter was healthy at this point in time.
        </Text>
      ) : (
        <VStack spacing={3} align="stretch">
          {issues.map((issue, index) => (
            <Box key={issue.event || index} p={3} borderRadius="lg" bg={cardBg}>
              <Flex align="center" wrap="wrap" mb={2}>
                <Badge mr={3} textTransform="uppercase">
                  {issue.type || "unknown"}
                </Badge>
                <Text fontWeight="bold">{issue.reason || "—"}</Text>
              </Flex>

              <Flex wrap="wrap" rowGap={2} mb={2}>
                <Box mr={6}>
                  <Text fontSize="xs" color={secondaryTextColor}>
                    Started
                  </Text>
                  <Text fontSize="sm">{formatDate(issue.started)}</Text>
                </Box>
                <Box mr={6}>
                  <Text fontSize="xs" color={secondaryTextColor}>
                    Completed
                  </Text>
                  <Text fontSize="sm">{formatDate(issue.completed)}</Text>
                </Box>
                {issue.event && (
                  <Box>
                    <Text fontSize="xs" color={secondaryTextColor}>
                      Event ID
                    </Text>
                    <Text fontSize="sm">{issue.event}</Text>
                  </Box>
                )}
              </Flex>

              {issue.errors?.length > 0 && (
                <Accordion allowToggle>
                  {issue.errors.map((error, errorIndex) => (
                    <AccordionItem key={errorIndex} border="none">
                      <AccordionButton px={0}>
                        <Box flex="1" textAlign="left">
                          <Text fontSize="sm" fontWeight="bold">
                            {error.name || "Error"}:{" "}
                            {error.message || "Unknown error"}
                          </Text>
                        </Box>
                        <AccordionIcon />
                      </AccordionButton>
                      <AccordionPanel px={0}>
                        {error.stack && (
                          <Code
                            display="block"
                            whiteSpace="pre-wrap"
                            fontSize="xs"
                            p={3}
                            borderRadius="md"
                            overflowX="auto"
                          >
                            {error.stack}
                          </Code>
                        )}
                        <CauseChain
                          cause={error.cause}
                          secondaryTextColor={secondaryTextColor}
                        />
                      </AccordionPanel>
                    </AccordionItem>
                  ))}
                </Accordion>
              )}
            </Box>
          ))}
        </VStack>
      )}

      <CommentInput
        statusId={record._id}
        comments={record.comments}
        onCommentsChange={onCommentsChange}
      />
    </Box>
  );
};

function PMSStatusMonitoring() {
  const [businesses, setBusinesses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedRange, setSelectedRange] = useState("Last 24 hours");
  const [statusFilter, setStatusFilter] = useState("");

  const [dateRange, setDateRange] = useState([
    moment().subtract(24, "hours").toDate(),
    moment().toDate(),
  ]);
  const [startDate, endDate] = dateRange;
  const theme = useTheme();
  const { colorMode } = useColorMode();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [expandedIndices, setExpandedIndices] = useState([]);
  // Which record rows have their inline detail expanded, tracked by record _id.
  const [expandedRecordIds, setExpandedRecordIds] = useState([]);
  // Email notification preferences for PMS health activity (server-owned).
  const [notifyOn, setNotifyOn] = useState({
    stateTransition: false,
    error: false,
  });
  const toast = useToast();
  const bid = selectedLocation?._id?.toString() || "";

  const toggleRecord = (id) =>
    setExpandedRecordIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
    );

  // Replace the comments on a single record (by status _id) in place — used
  // after posting a comment. The next listing refresh re-fetches everything.
  const updateRecordComments = (statusId, comments) =>
    setBusinesses((prev) =>
      prev.map((business) => ({
        ...business,
        statuses: (business.statuses || []).map((status) =>
          status._id === statusId ? { ...status, comments } : status
        ),
      }))
    );

  const updateUrlHash = (startDate, endDate, selectedRange, bid, status) => {
    const queryParams = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      selectedRange,
      bid,
      status,
    }).toString();
    window.location.hash = queryParams;
  };

  const fetchStatuses = async (params = {}, { background = false } = {}) => {
    // Background polls refresh data in place without flashing the full-page
    // spinner, so the table stays visible while it updates.
    if (!background) {
      setIsLoading(true);
    }

    try {
      const formattedStartDate = params.startDate
        ? moment(params.startDate).format("L")
        : moment(dateRange[0]).format("L");
      const formattedEndDate = params.endDate
        ? moment(params.endDate).format("L")
        : moment(dateRange[1]).format("L");
      const bidParam = params.bid ?? bid;
      const statusParam = params.status ?? statusFilter;

      const query = new URLSearchParams({
        rangeBeg: formattedStartDate,
        rangeEnd: formattedEndDate,
      });
      if (bidParam) query.set("bid", bidParam);
      if (statusParam) query.set("status", statusParam);

      const response = await cqAPI.get(
        `/monitoring/pms-statuses?${query.toString()}`
      );

      setBusinesses(response.data.businesses || []);
      if (response.data.notifyOn) {
        setNotifyOn(response.data.notifyOn);
      }
      setLastUpdated(new Date());
    } catch (error) {
      console.error("Error fetching PMS statuses:", error);
    } finally {
      if (!background) {
        setIsLoading(false);
      }
    }
  };

  const loadOptions = useCallback(
    debounce((inputValue, callback) => {
      loadCustomers(inputValue).then((options) => callback(options));
    }, 500),
    []
  );

  const DropdownIndicator = (props) => {
    return (
      components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
          <FontAwesomeIcon icon={faSearch} />
        </components.DropdownIndicator>
      )
    );
  };

  const formatLocationOption = (option) => (
    <Flex direction="column" py={1}>
      <Flex align="center">
        <Text>{option.name}</Text>
      </Flex>
    </Flex>
  );

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    setDateRange(computeRangeDates(value));
  };

  // Run a search with the current filters. For a named (non-Custom) range we
  // recompute the window from "now" and slide the visible date inputs to match,
  // so repeated/auto refreshes keep tracking the present rather than staying
  // pinned to whenever the page was first opened.
  const runSearch = (options = {}) => {
    let rangeStart = startDate;
    let rangeEnd = endDate;
    if (selectedRange !== "Custom") {
      [rangeStart, rangeEnd] = computeRangeDates(selectedRange);
      setDateRange([rangeStart, rangeEnd]);
    }
    return fetchStatuses({ startDate: rangeStart, endDate: rangeEnd }, options);
  };

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <Flex
      direction="column"
      width="100%"
      paddingLeft="20px"
      paddingRight="20px"
    >
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box
          p={2}
          borderRadius="md"
          borderWidth="2px"
          borderColor="purple.500"
          mr={2}
        >
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingTop="10px"
      >
        <Box
          as="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>
          {date.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </Box>
        <Box
          as="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  useEffect(() => {
    // On load, seed the filters from the URL hash (so a shared/bookmarked link
    // restores the same view) and fetch.
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const startDate = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().subtract(24, "hours").toDate();
    const endDate = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).toDate()
      : moment().toDate();
    const selectedRange = hashParams.get("selectedRange") || "Last 24 hours";
    const bid = hashParams.get("bid") || "";
    const status = hashParams.get("status") || "";

    setDateRange([startDate, endDate]);
    setSelectedRange(selectedRange);
    setStatusFilter(status);

    fetchStatuses({ startDate, endDate, selectedRange, bid, status });
  }, []);

  useEffect(() => {
    updateUrlHash(startDate, endDate, selectedRange, bid, statusFilter);
  }, [startDate, endDate, selectedRange, bid, statusFilter]);

  // Keep a stable handle to a background refresh that always closes over the
  // current filters, so the polling interval (set up once) never goes stale.
  const refreshRef = useRef(() => {});
  useEffect(() => {
    // A failed poll must never break the 60s cadence. fetchStatuses already
    // swallows API/network errors, but we belt-and-braces guard the whole call
    // here too: a synchronous throw (e.g. from the computeRangeDates/setDateRange
    // work in runSearch) or a rejected promise is caught and logged rather than
    // bubbling out of the interval tick. setInterval is never cleared on failure,
    // so the next attempt still fires ~60s later.
    refreshRef.current = () => {
      try {
        Promise.resolve(runSearch({ background: true })).catch((error) => {
          console.error("PMS auto-refresh failed; retrying next interval:", error);
        });
      } catch (error) {
        console.error("PMS auto-refresh failed; retrying next interval:", error);
      }
    };
  });

  // Poll every POLL_INTERVAL_MS while the page is visible. Switching tabs (or
  // otherwise hiding the page) pauses the timer; returning resumes it and
  // refreshes immediately. The interval is also torn down on unmount.
  useEffect(() => {
    let intervalId = null;

    const startPolling = () => {
      if (intervalId) {
        return;
      }
      intervalId = setInterval(() => refreshRef.current(), POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshRef.current();
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (document.visibilityState === "visible") {
      startPolling();
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Toggle one email-notification preference. Updates the UI optimistically and
  // persists the whole notifyOn object via PUT, reverting if the save fails.
  const handleNotifyToggle = async (key, checked) => {
    const previous = notifyOn;
    const updated = { ...notifyOn, [key]: checked };
    setNotifyOn(updated);

    try {
      await cqAPI.put("/monitoring/pms-status-notify", { notifyOn: updated });
    } catch (error) {
      console.error("Error updating notification preferences:", error);
      setNotifyOn(previous);
      toast({
        title: "Could not update notification settings",
        description: "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  // Mute or unmute PMS health alerts (email + Slack) for one business.
  // `duration` is one of MUTE_OPTIONS' keys, or "off" to unmute. The mute is
  // server-owned: the PUT responds with the resulting mute (or null), which
  // replaces the row's value; the 60s auto-refresh keeps it in sync after that.
  const handleMuteChange = async (targetBid, duration) => {
    try {
      const response = await cqAPI.put("/monitoring/pms-status-mute", {
        bid: targetBid,
        duration,
      });
      if (response.type === "ok") {
        setBusinesses((prev) =>
          prev.map((business) =>
            business.bid === targetBid
              ? { ...business, mute: response.data.mute }
              : business
          )
        );
      }
    } catch (error) {
      console.error("Error updating PMS alert mute:", error);
      toast({
        title: "Could not update alert muting",
        description: "Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
        position="relative"
      >
        <Spinner className="centered-spinner" color="blue.500" size="xl" />
      </Flex>
    );
  }

  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  return (
    <Box p={4} width="100%">
      <Flex justifyContent="flex-start" mb={4} wrap="wrap">
        <Flex align="center" mr={2} mb={2}>
          <Select
            placeholder="Select range"
            value={selectedRange}
            onChange={(e) => handleDateRangeSelection(e.target.value)}
            className="date-type"
          >
            <option value="Last 24 hours">Last 24 hours</option>
            <option value="Today">Today</option>
            <option value="Yesterday">Yesterday</option>
            <option value="Last 7 days">Last 7 days</option>
            <option value="Last 4 weeks">Last 4 weeks</option>
            <option value="Custom">Custom</option>
          </Select>
        </Flex>
        <Flex align="center" mr={2} mb={2}>
          <DatePicker
            className={`custom-datepicker ${
              colorMode === "light" ? "light-theme" : "dark-theme"
            }`}
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => {
              setDateRange(update);
              setSelectedRange("Custom");
            }}
            isClearable={true}
            renderCustomHeader={renderCustomHeader}
          />
        </Flex>
        <Flex align="center" mr={2} mb={2}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            placeholder="All statuses"
          >
            <option value="healthy">Healthy</option>
            <option value="impaired">Impaired</option>
            <option value="failed">Failed</option>
          </Select>
        </Flex>
        <Flex align="center" mr={2} mb={2}>
          <AsyncSelect
            loadOptions={loadOptions}
            value={selectedLocation}
            formatOptionLabel={formatLocationOption}
            placeholder="Search Locations"
            components={{ DropdownIndicator }}
            className="search-input"
            id="search-input"
            isClearable
            styles={{
              container: (base) => ({ ...base, minWidth: "180px" }),
            }}
            onChange={(opt) => setSelectedLocation(opt)}
          />
        </Flex>
        <Flex align="center" ml="auto" mb={2}>
          <Tooltip
            label={`Auto-refreshes every ${
              POLL_INTERVAL_MS / 1000
            } seconds while this page is open.${
              lastUpdated
                ? ` Last updated ${moment(lastUpdated).format("h:mm:ss A")}.`
                : ""
            }`}
            hasArrow
            placement="top"
          >
            <Button
              leftIcon={<FontAwesomeIcon icon={faHeartPulse} />}
              onClick={() => runSearch()}
            >
              Show statuses
            </Button>
          </Tooltip>
        </Flex>
      </Flex>

      <Flex align="center" wrap="wrap" rowGap={2} mb={4}>
        <Text mr={4}>Notify me when:</Text>
        <Tooltip
          label="Get an email whenever a location's health status changes — for example, when it goes from healthy to failed, or recovers back to healthy."
          hasArrow
          placement="top"
        >
          <Box as="span" mr={6}>
            <Checkbox
              isChecked={!!notifyOn.stateTransition}
              onChange={(e) =>
                handleNotifyToggle("stateTransition", e.target.checked)
              }
            >
              Health status changes
            </Checkbox>
          </Box>
        </Tooltip>
        <Tooltip
          label="Get an email every time an individual error occurs at any location, even if the overall health rating doesn't change."
          hasArrow
          placement="top"
        >
          <Box as="span">
            <Checkbox
              isChecked={!!notifyOn.error}
              onChange={(e) => handleNotifyToggle("error", e.target.checked)}
            >
              Any error occurs
            </Checkbox>
          </Box>
        </Tooltip>
      </Flex>

      {businesses.length === 0 ? (
        <Text color="gray.500">No PMS statuses found for this range.</Text>
      ) : (
        <Accordion
          allowMultiple
          index={expandedIndices}
          onChange={setExpandedIndices}
        >
          {businesses.map((business) => {
            const records = [...(business.statuses || [])].sort(
              (a, b) => new Date(b.created) - new Date(a.created)
            );
            const latest = records[0];
            const hasUnhealthy = records.some(
              (record) => record.status && record.status !== "healthy"
            );
            // Most recent moment any issue finished, across the business's
            // records in this range (issues[].completed).
            const lastIssueAt = records
              .flatMap((record) => record.issues || [])
              .map((issue) => issue?.completed)
              .filter(Boolean)
              .sort()
              .pop();
            const lastIssueText = lastIssueAt
              ? ` Last issue detected on ${moment(lastIssueAt).format("LLL")}.`
              : "";
            const tooltipLabel =
              business.latest_status === "healthy" && hasUnhealthy
                ? `Healthy now, but had issues during the selected period.${lastIssueText}`
                : `${
                    SYNC_HEALTH_LABELS[business.latest_status] ||
                    SYNC_HEALTH_LABELS.unknown
                  }${hasUnhealthy ? lastIssueText : ""}`;
            const mute = business.mute;
            return (
              <AccordionItem key={business.bid}>
                <Flex align="center">
                  <Tooltip label={tooltipLabel} hasArrow placement="top">
                    <AccordionButton _hover={{ bg: hoverBgColor }}>
                      <Flex flex="1" align="center" textAlign="left">
                        <Circle
                          size="12px"
                          bg={syncHealthColor(business.latest_status)}
                          mr={3}
                        />
                        <Text fontWeight="bold" mr={2}>
                          {business.business_name || business.bid}
                        </Text>
                        <Tooltip
                          label="Open this location in a new tab"
                          hasArrow
                          placement="top"
                        >
                          <Link
                            href={`/?bid=${business.bid}`}
                            isExternal
                            mr={3}
                            color="blue.500"
                            // Don't let the link click toggle the accordion row.
                            onClick={(e) => e.stopPropagation()}
                          >
                            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                          </Link>
                        </Tooltip>
                        {hasUnhealthy &&
                          business.latest_status !== "impaired" &&
                          business.latest_status !== "failed" && (
                          <Badge
                            colorScheme="orange"
                            display="flex"
                            alignItems="center"
                            mr={3}
                          >
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            <Text as="span" ml={1}>
                              Had issues
                            </Text>
                          </Badge>
                        )}
                        <Badge mr={3} textTransform="capitalize">
                          {business.latest_status || "unknown"}
                        </Badge>
                        {mute && (
                          <Badge
                            colorScheme="purple"
                            display="flex"
                            alignItems="center"
                            mr={3}
                          >
                            <FontAwesomeIcon icon={faBellSlash} />
                            <Text as="span" ml={1}>
                              {muteSummary(mute).badge}
                            </Text>
                          </Badge>
                        )}
                        <Text fontSize="sm" color="gray.500" mr={3}>
                          {business.count} record{business.count === 1 ? "" : "s"}
                        </Text>
                        {latest?.last_sync && (
                          <Text fontSize="sm" color="gray.500">
                            Last sync: {moment(latest.last_sync).format("LLL")}
                          </Text>
                        )}
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>
                  </Tooltip>
                  <MuteMenu
                    mute={mute}
                    onChange={(duration) =>
                      handleMuteChange(business.bid, duration)
                    }
                  />
                </Flex>
                <AccordionPanel pb={4}>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th width="2.5rem" />
                        <Th>Recorded</Th>
                        <Th>Status</Th>
                        <Th>Last successful sync</Th>
                        <Th>Issues</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {records.map((record) => {
                        const isExpanded = expandedRecordIds.includes(
                          record._id
                        );
                        return (
                          <React.Fragment key={record._id}>
                            <Tr
                              _hover={{ bg: hoverBgColor, cursor: "pointer" }}
                              bg={isExpanded ? hoverBgColor : undefined}
                              onClick={() => toggleRecord(record._id)}
                            >
                              <Td>
                                <Box
                                  as="span"
                                  display="inline-flex"
                                  color="gray.500"
                                  transition="transform 0.15s"
                                  transform={
                                    isExpanded
                                      ? "rotate(90deg)"
                                      : "rotate(0deg)"
                                  }
                                >
                                  <FontAwesomeIcon icon={faChevronRight} />
                                </Box>
                              </Td>
                              <Td>{moment(record.created).format("LLL")}</Td>
                              <Td>
                                <Tooltip
                                  label={
                                    SYNC_HEALTH_LABELS[record.status] ||
                                    SYNC_HEALTH_LABELS.unknown
                                  }
                                  hasArrow
                                  placement="top"
                                >
                                  <Flex align="center" width="fit-content">
                                    <Circle
                                      size="10px"
                                      bg={syncHealthColor(record.status)}
                                      mr={2}
                                    />
                                    <Text textTransform="capitalize">
                                      {record.status || "unknown"}
                                    </Text>
                                  </Flex>
                                </Tooltip>
                              </Td>
                              <Td>
                                {record.last_sync
                                  ? moment(record.last_sync).format("LLL")
                                  : "—"}
                              </Td>
                              <Td>{record.issues?.length || 0}</Td>
                            </Tr>
                            {isExpanded && (
                              <Tr>
                                <Td
                                  colSpan={5}
                                  p={0}
                                  bg={hoverBgColor}
                                  borderBottomWidth="2px"
                                >
                                  <PMSStatusInlineDetail
                                    record={record}
                                    onCommentsChange={(comments) =>
                                      updateRecordComments(record._id, comments)
                                    }
                                  />
                                </Td>
                              </Tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Tbody>
                  </Table>
                </AccordionPanel>
              </AccordionItem>
            );
          })}
        </Accordion>
      )}
    </Box>
  );
}

export default PMSStatusMonitoring;
